/**
 * 内容库入口。
 *
 * 三条硬约束：
 * 1. 默认内容包必须是 SFW 的：仓库里只带 `mild`(温和) 与 `flirty`(暧昧) 两档；
 *    `adult`(18+) 档在仓库里是**空的**，只能由运营方在后台自行导入（JSON 导入）。
 * 2. adultMode 关闭时，任何档位的请求都不会拿到 tier=2 的内容。
 * 3. 代码里不出现任何内容文案；文案只存在于 JSON 内容库。
 */

import cardsSeedRaw from "./content/cards.seed.json";
import danmakuSeedRaw from "./content/danmaku.seed.json";
import gamesRaw from "./content/games.json";
import { cardsFileSchema, danmakuFileSchema, gamesFileSchema } from "./schemas.ts";
import type { Card, CardPack, CardsFile, DanmakuFile, DanmakuItem, GameConfig, Tier } from "./types.ts";

function mustParse<T>(label: string, result: { success: true; data: T } | { success: false; error: unknown }): T {
  if (!result.success) {
    throw new Error(`内置内容库 ${label} 校验失败：${JSON.stringify(result.error)}`);
  }
  return result.data;
}

export const SEED_CARDS: CardsFile = mustParse("cards.seed.json", cardsFileSchema.safeParse(cardsSeedRaw));
export const SEED_DANMAKU: DanmakuFile = mustParse(
  "danmaku.seed.json",
  danmakuFileSchema.safeParse(danmakuSeedRaw),
);
export const GAMES: GameConfig[] = mustParse("games.json", gamesFileSchema.safeParse(gamesRaw)).games;

export const TIER_NAMES: Record<Tier, string> = { 0: "温和", 1: "暧昧", 2: "成人" };

/** 仓库内置的成人档占位包（永远为空，等运营方导入） */
export const EMPTY_ADULT_PACK: CardPack = { id: "adult", name: "成人", tier: 2, cards: [] };

export interface ContentQuery {
  tier: Tier;
  /** 是否允许 tier=2 的内容通过 */
  adultMode: boolean;
  /** 只取带这些标签的卡（任一命中） */
  tags?: string[];
}

/** 从内容库挑出可用卡池 */
export function selectCards(file: CardsFile, query: ContentQuery): Card[] {
  if (query.tier === 2 && !query.adultMode) return [];
  const tags = query.tags?.filter(Boolean) ?? [];
  const out: Card[] = [];
  for (const pack of file.packs) {
    if (pack.tier !== query.tier) continue;
    for (const card of pack.cards) {
      if (tags.length > 0 && !card.tags.some((t) => tags.includes(t))) continue;
      out.push(card);
    }
  }
  return out;
}

/** 弹幕池：同样受成人模式约束 */
export function selectDanmaku(file: DanmakuFile, adultMode: boolean, allTiers = true): DanmakuItem[] {
  if (allTiers) return file.danmaku.slice();
  // 弹幕默认不参与 tier 分级（没有 tier 字段），只有成人模式关闭时按 intensity 收敛
  return adultMode ? file.danmaku.slice() : file.danmaku.filter((d) => d.intensity <= 2);
}

export function cardById(file: CardsFile, id: string): Card | undefined {
  for (const pack of file.packs) {
    const found = pack.cards.find((c) => c.id === id);
    if (found) return found;
  }
  return undefined;
}

export function danmakuById(file: DanmakuFile, id: string): DanmakuItem | undefined {
  return file.danmaku.find((d) => d.id === id);
}

export function allTags(file: CardsFile): string[] {
  const set = new Set<string>();
  for (const pack of file.packs) for (const card of pack.cards) for (const tag of card.tags) set.add(tag);
  return [...set].sort();
}

/** 空内容库（后台清库/首次初始化时用） */
export function emptyCardsFile(): CardsFile {
  return { version: 1, packs: [] };
}

export function emptyDanmakuFile(): DanmakuFile {
  return { version: 1, danmaku: [] };
}
